import {
  useCreateProjectMutation,
  useProjectVersionPushMutation,
  useProjectVersionFinalizeMutation,
  useDeleteProjectMutation,
} from '../../../../generated/graphql';
import { useApolloClient } from '@apollo/client';
import { useNotification } from '../../notification/NotificationContext';
import { GET_PROJECTS } from '../../../../graphql/projects';
import { useProjectsContext } from '../context/ProjectsContext';

import { useProjectDirectories } from '../../../../context/ProjectDirectoriesContext';
import {
  useIsElectron,
  useElectronAPI,
} from '../../../../context/ElectronContext';
import {
  selectFolderAndDetectProjects,
  detectProjectsAtElectronPath,
  selectFolderForStems,
} from '../../../../common/utils/batchFolderSelection';
import {
  ProjectUploadItem,
  uploadSingleProject,
} from '../../../../common/utils/batchUploadFlow';
import { saveProjectLocationsBatch } from '../../../../common/utils/projectLocationSaver';
import { getProjectFiles } from '../../../../common/utils/projectDetection';
import { useTransferManagerContext } from '../../../../context/TransferManagerContext';
import { reportTransferError } from '@/services/transferDiagnostics';

interface UseBatchUploadHandlerProps {
  onProjectsRefetch?: () => void; // Optional, will use queryClient if not provided
}

export const useBatchUploadHandler = ({
  onProjectsRefetch,
}: UseBatchUploadHandlerProps = {}) => {
  const apolloClient = useApolloClient();
  const {
    setDetectedProjects,
    setUploadFiles,
    setUploadTypeDialogOpen,
    setBatchUploadDialogOpen,
    uploadFiles,
    setIsBatchUploading,
    setBatchUploadProgress,
    rootDirHandle,
    setRootDirHandle,
    rootPath,
    setRootPath,
    setIsScanning,
    setCurrentScanningFile,
  } = useProjectsContext();

  const { showSuccess, showError } = useNotification();
  const [createProject] = useCreateProjectMutation();
  const [projectVersionPush] = useProjectVersionPushMutation();
  const [projectVersionFinalize] = useProjectVersionFinalizeMutation();
  const [deleteProject] = useDeleteProjectMutation();
  const { setProjectDirectory } = useProjectDirectories();
  const isElectron = useIsElectron();
  const electronAPI = useElectronAPI();
  const { enqueueCreate } = useTransferManagerContext();

  const getTransferFileName = (file: File, folderPath: string) => {
    const maybeRelativePath = (file as File & { webkitRelativePath?: string })
      .webkitRelativePath;

    if (maybeRelativePath?.startsWith(`${folderPath}/`)) {
      return maybeRelativePath.slice(folderPath.length + 1);
    }

    return file.name;
  };

  const handleBatchFolderSelect = () => {
    setUploadTypeDialogOpen(true);
  };

  const handleProjectFilesFolderSelect = async () => {
    // Show loading spinner on button
    setIsScanning(true);
    setCurrentScanningFile(null);

    try {
      const result = await selectFolderAndDetectProjects(
        isElectron,
        electronAPI,
        {
          onFileScanned: (filePath: string) => setCurrentScanningFile(filePath),
          onError: (message: string) => {
            showError(message);
            reportTransferError('Discover project files', message);
          },
        }
      );

      if (result) {
        setDetectedProjects(result.detectedProjects);
        setUploadFiles(result.fileList);
        setRootDirHandle(result.rootDirHandle);
        setRootPath(result.rootPath);
        setBatchUploadDialogOpen(true);
      }
    } finally {
      setIsScanning(false);
      setCurrentScanningFile(null);
    }
  };

  const handleDetectedProjectPath = async (
    projectPath: string,
    projectFilePath?: string
  ) => {
    if (!electronAPI) return;
    setIsScanning(true);
    setCurrentScanningFile(null);
    try {
      const result = await detectProjectsAtElectronPath(
        electronAPI,
        projectPath,
        projectFilePath,
        {
          onFileScanned: (filePath: string) => setCurrentScanningFile(filePath),
          onError: (message: string) => {
            showError(message);
            reportTransferError('Discover project files', message);
          },
        }
      );
      if (result) {
        setDetectedProjects(result.detectedProjects);
        setUploadFiles(result.fileList);
        setRootDirHandle(result.rootDirHandle);
        setRootPath(result.rootPath);
        setBatchUploadDialogOpen(true);
      }
    } finally {
      setIsScanning(false);
      setCurrentScanningFile(null);
    }
  };

  const handleStemsFolderSelect = async () => {
    setIsScanning(true);
    setCurrentScanningFile(null);

    try {
      const result = await selectFolderForStems(isElectron, electronAPI, {
        onFileScanned: (filePath: string) => setCurrentScanningFile(filePath),
        onError: (message: string) => {
          showError(message);
          reportTransferError('Discover project files', message);
        },
      });

      if (result) {
        setDetectedProjects(result.detectedProjects);
        setUploadFiles(result.fileList);
        setRootDirHandle(result.rootDirHandle);
        setRootPath(result.rootPath);
        setBatchUploadDialogOpen(true);
      }
    } finally {
      setIsScanning(false);
      setCurrentScanningFile(null);
    }
  };

  const handleBatchUpload = async (selectedProjects: ProjectUploadItem[]) => {
    if (!uploadFiles) return;

    setIsBatchUploading(true);
    setBatchUploadProgress({});
    // Hand off progress visibility to the transfer sidebar immediately.
    setBatchUploadDialogOpen(false);

    let createdProjects: Array<{
      projectId: string;
      folderPath: string;
      projectName: string;
    }> = [];
    type CreatedProjectResult = {
      projectId: string;
      folderPath: string;
      projectName: string;
    };
    let hasErrors = false;

    try {
      const uploadJobPromises: Array<Promise<CreatedProjectResult | null>> =
        selectedProjects.map(async (projectItem) => {
          const projectFiles = getProjectFiles(
            uploadFiles,
            projectItem.detectedProject.folderPath
          );

          let createdProjectForJob: CreatedProjectResult | null = null;

          try {
            await enqueueCreate({
              projectId: `create:${projectItem.projectName}`,
              projectName: projectItem.projectName,
              files: projectFiles.map((file) => ({
                fileName: getTransferFileName(
                  file,
                  projectItem.detectedProject.folderPath
                ),
                size: file.size,
              })),
              successMessage: `Project "${projectItem.projectName}" uploaded successfully!`,
              errorMessage: `Failed to upload project "${projectItem.projectName}". Please try again.`,
              run: async (callbacks) => {
                createdProjectForJob = await uploadSingleProject(
                  projectItem,
                  uploadFiles,
                  isElectron,
                  electronAPI,
                  createProject,
                  projectVersionPush,
                  projectVersionFinalize,
                  deleteProject,
                  (projectName, progress) => {
                    setBatchUploadProgress((prev) => ({
                      ...prev,
                      [projectName]: progress,
                    }));
                  },
                  callbacks
                );
              },
            });

            return createdProjectForJob;
          } catch (error) {
            console.error(
              `Error creating/uploading project ${projectItem.projectName}:`,
              error
            );
            hasErrors = true;
            return null;
          }
        });

      // Queueing is complete at this point; release dialog/UI lock immediately.
      setIsBatchUploading(false);
      setBatchUploadProgress({});
      setUploadFiles(null);
      setDetectedProjects([]);
      setRootDirHandle(null);
      setRootPath(undefined);

      // Finalization runs in the background while transfers continue in sidebar.
      void Promise.all(uploadJobPromises)
        .then((completedProjects) => {
          createdProjects = completedProjects.filter(
            (project): project is CreatedProjectResult => project !== null
          );

          if (createdProjects.length > 0) {
            saveProjectLocationsBatch(
              createdProjects,
              isElectron,
              rootDirHandle,
              rootPath,
              setProjectDirectory
            ).catch((error) => {
              console.warn('Failed to save project locations:', error);
            });
          }

          // Refetch projects - use provided callback or apolloClient
          if (onProjectsRefetch) {
            onProjectsRefetch();
          } else {
            apolloClient.refetchQueries({ include: [GET_PROJECTS] });
          }

          if (!hasErrors && createdProjects.length > 0) {
            showSuccess(
              `Batch upload completed! ${createdProjects.length} project${
                createdProjects.length > 1 ? 's' : ''
              } uploaded successfully.`
            );
          } else if (hasErrors) {
            showError(
              `Upload failed. ${createdProjects.length} of ${selectedProjects.length} project${
                selectedProjects.length > 1 ? 's' : ''
              } uploaded. Please try again.`
            );
          }
        })
        .catch((error) => {
          console.error('Batch upload finalization error:', error);
          const errorMessage =
            error instanceof Error
              ? error.message
              : 'An error occurred during batch upload';
          showError(`Batch upload failed: ${errorMessage}. Please try again.`);
        });
    } catch (error) {
      console.error('Batch upload error:', error);
      hasErrors = true;
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'An error occurred during batch upload';
      showError(`Batch upload failed: ${errorMessage}. Please try again.`);
      setIsBatchUploading(false);
    }
  };

  return {
    handleBatchFolderSelect,
    handleProjectFilesFolderSelect,
    handleDetectedProjectPath,
    handleStemsFolderSelect,
    handleBatchUpload,
  };
};
